/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_SITE_CONTENT, SITE_CONTENT_ROW_ID } from "./defaultContent";
import { mergeContent } from "./mergeContent";
import type { SiteContent } from "./types";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

interface SiteContentContextValue {
  content: SiteContent;
  loading: boolean;
  isSupabaseReady: boolean;
  refresh: () => Promise<void>;
  setContent: (next: SiteContent) => void;
  saveContent: (nextContent?: SiteContent) => Promise<{ ok: boolean; message: string }>;
}

const SiteContentContext = createContext<SiteContentContextValue | undefined>(undefined);

export function SiteContentProvider({ children }: { children: React.ReactNode }) {
  const [content, setContentState] = useState<SiteContent>(DEFAULT_SITE_CONTENT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isSupabaseConfigured || !supabase) {
      setContentState(DEFAULT_SITE_CONTENT);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("site_content")
      .select("content")
      .eq("id", SITE_CONTENT_ROW_ID)
      .maybeSingle();

    if (error) {
      setContentState(DEFAULT_SITE_CONTENT);
      setLoading(false);
      return;
    }

    const merged = mergeContent(DEFAULT_SITE_CONTENT, data?.content ?? {});
    setContentState(merged);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.source !== "cms-admin-patch") return;
      const incoming = event.data.content;
      const merged = mergeContent(DEFAULT_SITE_CONTENT, incoming);
      setContentState(merged);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const setContent = useCallback((next: SiteContent) => {
    setContentState(next);
  }, []);

  const saveContent = useCallback(
    async (nextContent?: SiteContent) => {
      const payload = nextContent ?? content;
      if (!isSupabaseConfigured || !supabase) {
        return {
          ok: false,
          message: "Supabase bağlantısı yapılandırılmamış. .env.local dosyasını doldurun.",
        };
      }

      const { error } = await supabase.from("site_content").upsert(
        {
          id: SITE_CONTENT_ROW_ID,
          content: payload,
        },
        { onConflict: "id" },
      );

      if (error) {
        return { ok: false, message: error.message };
      }
      return { ok: true, message: "İçerik kaydedildi." };
    },
    [content],
  );

  const value = useMemo<SiteContentContextValue>(
    () => ({
      content,
      loading,
      isSupabaseReady: isSupabaseConfigured,
      refresh,
      setContent,
      saveContent,
    }),
    [content, loading, refresh, saveContent, setContent],
  );

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error("useSiteContent must be used within SiteContentProvider");
  }
  return context;
}
